import { useSyncExternalStore } from 'react';

/**
 * Hell oder dunkel: ohne eigene Wahl wie das System (`prefers-color-scheme`), sonst wie zuletzt mit dem Knopf in der Kopfleiste gewählt
 * (im Browser gemerkt). Die Wahl steht als `data-theme` am Wurzelelement (tokens.css richtet die Farben danach); Logo und Icons im
 * Browser-Tab folgen ihr (heller Satz unter `/…`, dunkler unter `/dark/…`, siehe vite.config.ts).
 */
export type ColorScheme = 'light' | 'dark';

const KEY = 'dispositio.theme';
/** Meldung an alle, die die Farben selbst umrechnen (Zeichenflächen) */
export const SCHEME_EVENT = 'dispositio:scheme';

/** Wahl ohne Speicher im Browser (privates Fenster): gilt bis zum Neuladen */
let memory: ColorScheme | null = null;

const media = (): MediaQueryList | null => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null);

/** Eigene Wahl; null: wie das System */
export function storedScheme(): ColorScheme | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return memory;
  }
}

/** Das Farbschema, das gerade gilt */
export const currentScheme = (): ColorScheme => storedScheme() ?? (media()?.matches ? 'dark' : 'light');

/** Icons, Manifest und Leistenfarbe des Browsers auf den Satz des Modus umstellen (die Adressen in index.html sind die hellen) */
function applyBrand(scheme: ColorScheme): void {
  const dark = scheme === 'dark';
  for (const link of document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"], link[rel="apple-touch-icon"], link[rel="manifest"]')) {
    const bright = link.dataset.bright ?? link.getAttribute('href') ?? '';
    link.dataset.bright = bright;
    const href = dark && bright.startsWith('/') && !bright.startsWith('/dark/') ? `/dark${bright}` : bright;
    if (link.getAttribute('href') !== href) link.setAttribute('href', href);
  }
  // die Leistenfarbe: mit eigener Wahl gilt sie unabhängig vom System
  const own = storedScheme();
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    const original = meta.dataset.media ?? meta.getAttribute('media') ?? '';
    meta.dataset.media = original;
    const mine = original.includes(scheme === 'dark' ? 'dark' : 'light');
    if (own) meta.setAttribute('media', mine ? 'all' : 'not all');
    else meta.setAttribute('media', original);
  }
}

function apply(): void {
  const own = storedScheme();
  if (own) document.documentElement.dataset.theme = own;
  else delete document.documentElement.dataset.theme;
  applyBrand(currentScheme());
  window.dispatchEvent(new Event(SCHEME_EVENT));
}

/** Eigene Wahl setzen (bzw. mit null wieder dem System folgen) */
export function setScheme(scheme: ColorScheme | null): void {
  memory = scheme;
  try {
    if (scheme) localStorage.setItem(KEY, scheme);
    else localStorage.removeItem(KEY);
  } catch {
    // gilt dann nur bis zum Neuladen (`memory`)
  }
  apply();
}

/** Zum anderen Modus wechseln (gemerkt) */
export const toggleScheme = (): void => setScheme(currentScheme() === 'dark' ? 'light' : 'dark');

/** Beim Start: gemerkte Wahl anwenden und dem System folgen, solange es keine gibt */
export function initColorScheme(): void {
  apply();
  media()?.addEventListener('change', apply);
}

/** Bei jedem Wechsel (Knopf oder System) aufrufen; liefert das Abmelden */
export function onSchemeChange(fn: () => void): () => void {
  window.addEventListener(SCHEME_EVENT, fn);
  return () => window.removeEventListener(SCHEME_EVENT, fn);
}

/** Das geltende Farbschema als React-Zustand */
export const useColorScheme = (): ColorScheme => useSyncExternalStore(onSchemeChange, currentScheme, () => 'light');
